"use client";

import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { WalletReadyState } from "@solana/wallet-adapter-base";

import {
  braveWalletFirst,
  isPhone,
  phantomBrowseUrl,
} from "@/lib/wallet/phantom-browse";
import {
  forgetReturning,
  forgetSignedOut,
  hasDeclinedSignIn,
  rememberDeclinedSignIn,
  rememberSignedOut,
} from "@/lib/wallet/phantom-trusted";
import {
  NOT_INSTALLED,
  PHANTOM_DOWNLOAD,
  BRAVE_WALLET_FIRST,
  PHANTOM_NOT_READY,
  PHANTOM_NOT_READY_PHONE,
  DECLINED,
  walletRefusal,
} from "@/lib/wallet/phantom-copy";
import { createPhantomProvider } from "@/lib/wallet/phantom-provider";
import { usePhantomReattach } from "@/lib/wallet/use-phantom-reattach";
import {
  WALLET_NOT_READY,
  useSolanaAdapter,
} from "@/lib/wallet/use-solana-adapter";

import type { SignInOutcome } from "@/lib/wallet/use-solana-sign-in";
import type { SolanaWallet } from "@/lib/wallet/solana-wallet";

/**
 * The two routes to a recorded sign-in, injected so this hook keeps to the
 * wallet and `use-solana-sign-in` keeps to the HTTP.
 */
export interface SignInRoutes {
  /** One approval: connects and authenticates together. */
  signInAtConnect: () => Promise<SignInOutcome>;
  /** The second-prompt fallback, once an address is known. */
  signInByMessage: (wallet: {
    address: string;
    signMessage: (message: Uint8Array) => Promise<Uint8Array>;
  }) => Promise<boolean>;
}

/**
 * Phantom, published as the same `SolanaWallet` the Glyph hook publishes.
 *
 * The adapter is an external store, so it is read with
 * `useSyncExternalStore` rather than mirrored into state. That is not a
 * stylistic choice: the extension can register after this mounts, and an
 * effect that copies the adapter into state would render once against a
 * snapshot that was already stale.
 *
 * The adapter's own `autoConnect` is deliberately unused: it calls
 * `connect()`, which asks the wallet, and an unrequested approval window on
 * page load is exactly what `use-evm-wallet` went out of its way to stop
 * happening on the ApeChain side.
 *
 * What runs instead asks the injected provider to reconnect
 * `onlyIfTrusted`, which resolves for a site the holder has already approved
 * and rejects for one they have not, raising no approval window either way.
 * It also waits, which is the point: Phantom reattaches a trusted site about
 * a second after the page loads, so reading `isConnected` on mount, as this
 * first did, read it while it was still false and left a returning holder on
 * a logged-out site. A holder who logged out stays logged out, since
 * Phantom's trust outlives our session and would otherwise drag them back
 * in.
 *
 * Account switching needs no handling of its own: the adapter re-emits
 * `connect` with the new key, so `solanaAddress` changes and every
 * owner-scoped guard downstream sees it. It does not record a new sign-in —
 * that would raise a signature prompt nobody asked for, in the middle of
 * whatever the holder was doing. The row follows the next deliberate connect.
 */
export function usePhantomWallet(
  signIn?: SignInRoutes,
  /** The returning-holder cookie, read by the server; see `phantom-trusted`. */
  returning = false
): SolanaWallet {
  const { adapter, isPhantom, signer } = useSolanaAdapter();

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      adapter.on("connect", onStoreChange);
      adapter.on("disconnect", onStoreChange);
      adapter.on("readyStateChange", onStoreChange);
      return () => {
        adapter.off("connect", onStoreChange);
        adapter.off("disconnect", onStoreChange);
        adapter.off("readyStateChange", onStoreChange);
      };
    },
    [adapter]
  );

  const address = useSyncExternalStore(
    subscribe,
    () => adapter.publicKey?.toBase58() ?? null,
    () => null
  );
  const readyState = useSyncExternalStore(
    subscribe,
    () => adapter.readyState,
    // No extension on the server, and none during the first paint.
    () => WalletReadyState.Unsupported
  );

  useEffect(() => {
    /* `error` has no default handler on eventemitter3, so this is for the
       record rather than to stop a throw. Failures a holder must see are
       raised by the call that caused them. */
    const onError = (error: unknown) =>
      console.warn("[phantom] wallet reported an error", error);
    adapter.on("error", onError);
    return () => void adapter.off("error", onError);
  }, [adapter]);

  const reattaching = usePhantomReattach({
    adapter,
    address,
    isPhantom,
    readyState,
    returning,
  });

  const connectSolana = useCallback(async () => {
    /* `Loadable` is the adapter's own mobile case: it redirects into
       Phantom's browser rather than connecting here, so it is not an error.
       It covers only an iOS user agent that also says Safari, which leaves
       Android and the iOS webviews reading as `NotDetected` — told to
       install an extension that, on a phone, does not exist. Send those the
       same way the adapter sends iOS. */
    if (
      readyState === WalletReadyState.NotDetected ||
      readyState === WalletReadyState.Unsupported
    ) {
      const browse =
        typeof window === "undefined"
          ? null
          : phantomBrowseUrl(window.location, window.navigator.userAgent);
      if (!browse) {
        /* A desktop browser with no Phantom: about 600 holders in six hours
           hit this as a dead end on /claim/. Open the download page on this
           same click, while it still counts as one, and say what comes next. */
        // Each deliberate retry must work after the download tab is closed.
        if (typeof window === "undefined") throw new Error(NOT_INSTALLED);
        window.open(PHANTOM_DOWNLOAD, "_blank", "noopener");
        throw new Error(
          braveWalletFirst() ? BRAVE_WALLET_FIRST : NOT_INSTALLED
        );
      }
      // Only ever after the holder asked to connect, so this leaves the page
      // on a deliberate gesture rather than on its own.
      window.location.href = browse;
      return;
    }

    /* `solana:signIn` first, which connects and authenticates in one
       approval. The adapter's own connect then finds the wallet already
       connected and asks for nothing, so this costs the holder one prompt
       rather than two. */
    /* After a refusal in this tab, the next Connect is a plain connect: the
       holder clicked again, which is consent to connecting, and asking them
       to sign in again is the question they already answered. */
    const declinedBefore = hasDeclinedSignIn();
    const outcome = declinedBefore
      ? "unavailable"
      : ((await signIn?.signInAtConnect()) ?? "unavailable");

    /* A refusal ends it here. Connecting anyway would raise a second prompt
       and the fallback a third, so saying no would cost a holder more
       prompts than saying yes — and the one thing this flow must never do is
       answer a refusal with another request. */
    if (outcome === "declined") {
      rememberDeclinedSignIn();
      throw new Error(DECLINED);
    }

    // Connecting on purpose undoes a previous log out.
    forgetSignedOut();
    try {
      await adapter.connect();
    } catch (error) {
      if (
        error instanceof Error &&
        // 4100 ("not been authorized by the user") on connect is the same
        // locked or unready Phantom, and its raw text told holders nothing.
        // So is -32603: four Brave holders read "An internal error has
        // occurred" up to five times each in a week (Faro, 2026-11-03).
        /^unexpected error$|has not been authorized by the user|^an internal error has occurred$/i.test(
          error.message.trim()
        )
      )
        throw new Error(
          !isPhantom
            ? WALLET_NOT_READY
            : isPhone(window.navigator.userAgent)
              ? PHANTOM_NOT_READY_PHONE
              : PHANTOM_NOT_READY,
          { cause: error }
        );
      throw walletRefusal(error, adapter.name) ?? error;
    }
    if (outcome === "signed-in" || declinedBefore) return;

    /* No `signIn` on this wallet. Fall back to a signed message, which is a
       second prompt — fire and forget, because the connection has succeeded
       either way and the row authorizes nothing. */
    const address = adapter.publicKey?.toBase58();
    if (!address || !signIn) return;
    void signIn.signInByMessage({
      address,
      signMessage: signer.signMessage,
    });
  }, [adapter, isPhantom, readyState, signIn, signer]);

  const disconnectSolana = useCallback(() => {
    // Remembered, because Phantom keeps trusting the site either way.
    rememberSignedOut();
    forgetReturning();
    /* The site forgets the wallet; the wallet does not forget the site. Which
       account the extension offers next is the extension's to decide, exactly
       as it is on the Glyph path. */
    void adapter.disconnect().catch(() => undefined);
  }, [adapter]);

  const solana = useMemo(
    () =>
      address
        ? createPhantomProvider({
            address,
            adapter: signer,
            name: adapter.name,
            advertisedTransactionVersions: adapter.supportedTransactionVersions
              ? [...adapter.supportedTransactionVersions]
              : undefined,
            telemetrySource: isPhantom ? "phantom-adapter" : "wallet-standard",
          })
        : null,
    [
      address,
      signer,
      adapter.name,
      adapter.supportedTransactionVersions,
      isPhantom,
    ]
  );

  // For a caller that needs the wallet proven now, such as the game naming a
  // holder Phantom reattached without a sign-in: one prompt, on their page.
  const proveWallet = useCallback(async () => {
    if (!address || !signIn) return false;
    return signIn.signInByMessage({
      address,
      signMessage: signer.signMessage,
    });
  }, [address, signIn, signer]);

  return useMemo(
    () => ({
      // Usable the moment it mounts; there is no SDK to boot.
      ready: true,
      solanaAddress: address,
      solana,
      connectSolana,
      disconnectSolana,
      proveWallet,
      reattaching,
      walletName: adapter.name,
    }),
    [
      adapter,
      address,
      connectSolana,
      disconnectSolana,
      proveWallet,
      reattaching,
      solana,
    ]
  );
}
